import { z } from "zod";
import { TFunction } from "i18next";
import {
  decryptPrivateKey,
  deriveKey,
  deriveKeyWithSalt,
  kekFromMk,
} from "@/lib/crypto/crypto";
import { useAuthState } from "@/zustand/AuthState";

export const getChangePasswordSchema = (
  username: string,
  ePrK: string,
  t: TFunction,
) =>
  z
    .object({
      oldPassword: z.string().min(6, {
        message: t("passwordToShort"),
      }),
      password: z.string().min(6, {
        message: t("passwordToShort"),
      }),
      confirmPassword: z.string().min(6, {
        message: t("passwordToShort"),
      }),
    })
    .refine(
      async (data) => {
        // The private key is wrapped under the master key (v1) or the HKDF kek
        // (v2/v3). v3 derives mk from the account's stored random salt; v1/v2
        // from the username. Build candidate KEKs for the account's scheme and
        // try each, so the old-password check is scheme-agnostic.
        const { kdfVersion, kdfSalt } = useAuthState.getState();
        // Discriminate on kdfVersion (matching recoveryKey.isPasswordValid) rather
        // than kdfSalt !== "" — keying both derivation checks off the same field
        // removes a latent trap where the two could disagree.
        // deriveKeyWithSalt throws on a malformed salt; this runs inside an async
        // `.refine` that must resolve to a boolean, so a throw would reject
        // validation unexpectedly. Treat a derivation failure as "old password
        // invalid".
        let mk: string;
        try {
          mk =
            kdfVersion >= 3
              ? deriveKeyWithSalt(data.oldPassword, kdfSalt)
              : deriveKey(data.oldPassword, username);
        } catch {
          return false;
        }
        for (const key of [mk, kekFromMk(mk)]) {
          try {
            await decryptPrivateKey(ePrK, key);
            return true;
          } catch {
            // try the next candidate key
          }
        }
        return false;
      },
      {
        message: t("changePassword.oldPasswordIsIncorrect"),
        path: ["oldPassword"],
      },
    )
    .refine((data) => data.password === data.confirmPassword, {
      message: t("changePassword.passwordMustMatchConfirmPassword"),
      path: ["confirmPassword"],
    })
    .refine((data) => data.password !== data.oldPassword, {
      message: t("changePassword.passwordMustDifferFromOldPassword"),
      path: ["password"],
    });
export type ChangePasswordType = z.infer<
  ReturnType<typeof getChangePasswordSchema>
>;
